import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const KnowYourGib = () => {
  // Individual metrics
  const individualMetrics = [
    { name: "Quality of Contributions (Q)", score: 9.5, weight: 0.30, description: "Peer-reviewed score for accuracy, relevance, and utility of knowledge contributions" },
    { name: "Collaboration (C)", score: 9.0, weight: 0.25, description: "Number of peer-builds, diversity of wallets involved, and feedback from peers" },
    { name: "Engagement (E)", score: 9.3, weight: 0.20, description: "Frequency and consistency of participation in knowledge base activities" },
    { name: "Performance with GenAI (P)", score: 9.1, weight: 0.15, description: "Effectiveness in using AI to generate impactful prompts and solutions" },
    { name: "Adaptability (A)", score: 9.1, weight: 0.10, description: "Ability to adjust to new tasks, challenges, or team dynamics" },
  ];

  // Team metrics
  const teamMetrics = [
    { name: "Team Average", score: 9.2 },
    { name: "Collaboration Diversity (D)", score: 9.4, weight: 0.10 },
    { name: "Peer Review Score (PRS)", score: 9.3, weight: 0.10 },
  ];

  // Calculate weighted individual score
  const calculateIndividualScore = () => {
    return individualMetrics.reduce((acc, metric) => {
      return acc + (metric.score * metric.weight);
    }, 0).toFixed(1);
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="p-8">
          <h1 className="font-serif text-3xl font-bold text-primary mb-8">Know Your Gib</h1>
          
          <Tabs defaultValue="individual" className="space-y-8">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="individual">Individual Rating</TabsTrigger>
              <TabsTrigger value="team">Team Rating</TabsTrigger>
            </TabsList>

            <TabsContent value="individual" className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="font-serif text-2xl font-semibold text-primary mb-2">Individual GibRating™</h2>
                <div className="flex items-center justify-center space-x-2">
                  <span className="text-5xl font-bold text-accent">{calculateIndividualScore()}</span>
                  <span className="text-xl text-muted-foreground">/10</span>
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="font-serif text-xl font-semibold text-primary mb-4">Component Breakdown</h3>
                {individualMetrics.map((metric) => (
                  <div key={metric.name} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-medium">{metric.name}</span>
                        <p className="text-sm text-muted-foreground">{metric.description}</p>
                      </div>
                      <div className="text-right">
                        <span className="font-mono">{metric.score}/10</span>
                        <p className="text-sm text-muted-foreground">Weight: {(metric.weight * 100)}%</p>
                      </div>
                    </div>
                    <Progress value={(metric.score / 10) * 100} className="h-2" />
                  </div>
                ))}
              </div>

              <div className="mt-8 p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2">Formula</h4>
                <p className="text-sm text-muted-foreground">
                  Individual GIB Rating = (0.30 × Q) + (0.25 × C) + (0.20 × E) + (0.15 × P) + (0.10 × A)
                </p>
              </div>
            </TabsContent>

            <TabsContent value="team" className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="font-serif text-2xl font-semibold text-primary mb-2">Team GibRating™</h2>
                <div className="flex items-center justify-center space-x-2">
                  <span className="text-5xl font-bold text-accent">9.3</span>
                  <span className="text-xl text-muted-foreground">/10</span>
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="font-serif text-xl font-semibold text-primary mb-4">Team Metrics</h3>
                {teamMetrics.map((metric) => (
                  <div key={metric.name} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{metric.name}</span>
                      <div className="text-right">
                        <span className="font-mono">{metric.score}/10</span>
                        {metric.weight && (
                          <p className="text-sm text-muted-foreground">Weight: {(metric.weight * 100)}%</p>
                        )}
                      </div>
                    </div>
                    <Progress value={(metric.score / 10) * 100} className="h-2" />
                  </div>
                ))}
              </div>

              <div className="mt-8 p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2">Enhanced Team Formula</h4>
                <p className="text-sm text-muted-foreground">
                  Team GIB Rating = (Average of Individual Ratings) + (0.10 × D) + (0.10 × PRS)
                </p>
              </div>
            </TabsContent>
          </Tabs>
        </Card>
      </main>
    </div>
  );
};

export default KnowYourGib;